import type { ReactNode } from 'react'
import { Navigate, Outlet, useLocation, useParams } from 'react-router'

import { useSession } from './session'

import { useMyWorkspaces } from '@/features/workspaces/queries'


function FullPageMessage({ children }: { children: ReactNode }) {
  return (
    <main
      style={{ minHeight: '100dvh', display: 'grid', placeItems: 'center' }}
      aria-live="polite"
    >
      <p style={{ color: 'var(--color-text-secondary)' }}>{children}</p>
    </main>
  )
}

/** Requires a session. Preserves where the user was going. */
export function RequireAuth() {
  const { status } = useSession()
  const location = useLocation()

  if (status === 'loading') return <FullPageMessage>Loading…</FullPageMessage>

  if (status === 'anonymous') {
    const next = encodeURIComponent(location.pathname + location.search)
    return <Navigate to={`/auth/signin?next=${next}`} replace />
  }

  return <Outlet />
}

/**
 * Requires membership of the workspace in the URL.
 *
 * A non-member is sent to the picker rather than shown a 403 — and the API
 * returns 404 for the same request, so nothing confirms the workspace exists.
 */
export function RequireWorkspace() {
  const { workspaceSlug } = useParams<{ workspaceSlug: string }>()
  const { data: workspaces, isLoading, isFetching, isError } = useMyWorkspaces()

  if (isLoading) return <FullPageMessage>Loading workspace…</FullPageMessage>
  if (isError) return <FullPageMessage>Couldn’t load your workspaces.</FullPageMessage>

  // `isLoading` is false for a query that has cached data and is refetching, so
  // it alone cannot answer "does this user have no workspaces, or have we just
  // not heard back yet?". Redirecting on the first reading sent anyone who had
  // just created their first workspace back to onboarding.
  if (!workspaces?.length) {
    if (isFetching) return <FullPageMessage>Loading workspace…</FullPageMessage>
    return <Navigate to="/onboarding" replace />
  }

  const match = workspaces.find((w) => w.slug === workspaceSlug)
  if (!match) return <Navigate to="/workspaces" replace />

  return <Outlet />
}

/**
 * Route entry: send the user somewhere sensible based on what they have.
 *
 *   no workspaces        → onboarding
 *   exactly one          → straight into it
 *   several              → the picker
 */
export function RootRedirect() {
  const { data: workspaces, isLoading } = useMyWorkspaces()

  if (isLoading) return <FullPageMessage>Loading…</FullPageMessage>
  if (!workspaces?.length) return <Navigate to="/onboarding" replace />
  if (workspaces.length === 1) return <Navigate to={`/w/${workspaces[0]!.slug}`} replace />
  return <Navigate to="/workspaces" replace />
}
